import { useEffect, useState } from "react";
import { apiClient, useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import AnimatedCounter from "@/components/AnimatedCounter";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import ActivityFeed from "@/components/ActivityFeed";
import WeeklyDigest from "@/components/WeeklyDigest";
import { ArrowRight, TrendingUp, Sparkles, Building2, Zap, Target } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function ScoreCard({ label, value, icon: Icon, gradient }) {
  return (
    <div className="group card-hover relative border border-border rounded-xl p-6 bg-card overflow-hidden" data-testid={`score-${label}`}>
      <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full ${gradient} opacity-50 blur-3xl group-hover:opacity-80 transition-opacity duration-500`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-black font-display tabular-nums">
            <AnimatedCounter value={value} duration={1100} />
          </span>
          <span className="text-sm text-muted-foreground">/ 100</span>
        </div>
        <div className="h-1.5 mt-4 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full transition-all duration-1000" style={{ width: `${value}%` }} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="relative border border-dashed border-border rounded-2xl p-10 sm:p-14 text-center overflow-hidden" data-testid="empty-state">
      <div className="absolute inset-0 dot-bg opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
      <div className="relative">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
          <Building2 className="h-7 w-7 text-primary" />
        </div>
        <h3 className="mt-6 text-2xl sm:text-3xl font-black font-display tracking-tighter">
          Démarrez votre <span className="font-serif-italic font-normal">aventure locale</span>
        </h3>
        <p className="mt-3 text-muted-foreground max-w-md mx-auto">
          Créez votre profil business pour activer le diagnostic, le suivi des scores et le studio de contenu — en 2 minutes.
        </p>
        <Link to="/dashboard/profile">
          <Button className="mt-7 rounded-full btn-glow h-11 px-6" onClick={onCreate} data-testid="create-business-cta">
            Créer mon profil<ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({ generations: 0, events: 0 });

  useEffect(() => {
    apiClient.get("/dashboard").then(r => setData(r.data)).catch(()=>{});
    Promise.all([apiClient.get("/ai/history"), apiClient.get("/calendar")]).then(([g, e]) => {
      setCounts({ generations: g.data.length, events: e.data.length });
    }).catch(()=>{});
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 shimmer rounded-md" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-36 shimmer rounded-xl" />)}
        </div>
        <div className="h-72 shimmer rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div className="flex flex-wrap items-end justify-between gap-3 fade-up">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Bonjour {user?.name?.split(" ")[0]}</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
            Pilotez votre <span className="font-serif-italic font-normal">croissance locale</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Votre commerce, vu sous tous les angles. Concentrez-vous sur ce qui fait grandir votre activité.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-soft" />
            Données à jour
          </div>
        </div>
      </div>

      {!data.has_business ? <EmptyState /> : (
        <>
          <OnboardingChecklist data={{ has_business: data.has_business, scores: data.scores, generations_count: counts.generations, events_count: counts.events }} />

          <WeeklyDigest />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up-1">
            <ScoreCard label="Score de visibilité" value={data.scores.ai_visibility_score} icon={Sparkles} gradient="bg-gradient-to-br from-primary/40 to-violet-500/40" />
            <ScoreCard label="Référencement local" value={data.scores.local_seo_score} icon={Target} gradient="bg-gradient-to-br from-blue-500/40 to-cyan-400/40" />
            <ScoreCard label="Engagement social" value={data.scores.social_media_score} icon={Zap} gradient="bg-gradient-to-br from-violet-500/40 to-pink-400/40" />
            <ScoreCard label="Profil complété" value={data.scores.profile_completion} icon={TrendingUp} gradient="bg-gradient-to-br from-emerald-400/40 to-blue-500/40" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 fade-up-2">
            <div className="lg:col-span-2 border border-border rounded-xl p-6 bg-card">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Croissance hebdomadaire</p>
                  <h3 className="font-bold text-lg mt-1.5">Visibilité · 8 dernières semaines</h3>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-500 font-medium px-2.5 py-1 bg-emerald-500/10 rounded-full">
                  <TrendingUp className="h-3 w-3" />en progression
                </div>
              </div>
              <div className="h-64 min-h-[256px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.weekly_growth} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "3 3" }}
                    />
                    <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#areaGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-border rounded-xl p-6 bg-card">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Recommandations</p>
              <h3 className="font-bold text-lg mt-1.5 mb-4">À faire maintenant</h3>
              <ul className="space-y-3">
                {data.recommendations.length === 0 && (
                  <li className="text-sm text-muted-foreground py-6 text-center">Aucune recommandation 🎉</li>
                )}
                {data.recommendations.map((r, i) => (
                  <li key={i} className="group border border-border rounded-lg p-3.5 hover:border-primary/40 transition-colors" data-testid={`reco-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${
                        r.priority === "haute" ? "text-rose-500" : r.priority === "moyenne" ? "text-amber-500" : "text-primary"
                      }`}>{r.priority}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <p className="font-semibold mt-1.5 text-sm">{r.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.detail}</p>
                  </li>
                ))}
              </ul>
              <Link to="/dashboard/audit" className="block mt-5">
                <Button variant="outline" className="w-full rounded-full" data-testid="full-audit-btn">
                  Audit complet<ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 fade-up-3">
            <div className="lg:col-span-2"><ActivityFeed /></div>
            <div className="relative overflow-hidden border border-border rounded-2xl p-7 grid gap-6 items-center bg-card">
              <div className="absolute inset-0 conic-bg opacity-50" />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center mb-4">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-display font-black text-xl sm:text-2xl tracking-tighter">
                  Studio de <span className="font-serif-italic font-normal">contenu</span>
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Rédigez vos prochaines publications en quelques secondes.
                </p>
                <Link to="/dashboard/generator" className="inline-block mt-4">
                  <Button className="rounded-full h-10 px-5 btn-glow" data-testid="goto-generator-btn">
                    Ouvrir<ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
